import React from "react";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Typography,
  Avatar,
  Button,
  InputBase,
  Badge,
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import SearchIcon from "@mui/icons-material/Search";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";

import { DRAWER_WIDTH, COLLAPSED_WIDTH } from "./layoutConstants";
import { getAuth } from "../auth";

const Header = ({ onToggleSidebar, isSidebarOpen }) => {
  const user = getAuth();
  const left = isSidebarOpen ? DRAWER_WIDTH : COLLAPSED_WIDTH;

  // BUG FIX (found while investigating why the Historical Task Entry
  // permission never refreshed after login): this only ever removed
  // `"user"` -- never `"token"` -- from localStorage. The stale token
  // survived logout, so AuthContext's own session-validation effect
  // (GET /auth/me, on next app load) would silently re-authenticate as the
  // PREVIOUS session's user using that still-valid JWT before anyone
  // logged back in, meaning `user?.id` in React state could stay non-null
  // (or land back on the same id) straight through a logout, defeating any
  // "already loaded for this user" check downstream (see
  // context/PermissionContext.js) and leaving stale permissions/feature
  // flags in place after a role/permission change. Both localStorage keys
  // set at login must be cleared together at logout.
  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("token_expires_at");
    window.location.href = "/login";
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: 1301,
        background: "rgba(255, 251, 252, 0.9)",
        backdropFilter: "blur(16px)",
        color: "var(--text-primary, #241f1a)",
        borderBottom: "1px solid var(--brand-cream-line, rgba(109, 21, 48, 0.12))",
        boxShadow: "0 8px 24px rgba(74, 14, 34, 0.06)",

        // Mobile par full width, desktop par sidebar ke according
        ml: {
          xs: 0,
          md: `${left}px`,
        },
        width: {
          xs: "100%",
          md: `calc(100% - ${left}px)`,
        },

        transition: "margin-left 220ms ease, width 220ms ease",
      }}
    >
      <Toolbar
        sx={{
          justifyContent: "space-between",
          minHeight: {
            xs: 62,
            sm: 72,
            md: 88,
          },
          px: {
            xs: 1,
            sm: 2,
            md: 3,
          },
          whiteSpace: "nowrap",
          gap: {
            xs: 1,
            sm: 1.5,
            md: 2,
          },
        }}
      >
        {/* Left Side */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: {
              xs: 0.8,
              sm: 1.2,
              md: 1.4,
            },
            minWidth: 0,
            // Grows from its natural width at every breakpoint (not just
            // xs) so it gains space in step with the right-side Box below
            // -- two equal-growth siblings around the fixed-width search
            // box is what centers it. justify-content:"space-between" alone
            // only equalizes the two gaps immediately touching the search
            // box, not the box's position relative to the header's true
            // midpoint, which drifts whenever the left (brand) and right
            // (profile/actions) content widths differ. flexBasis stays
            // "auto" (not the flex:1 shorthand's 0%) so this never shrinks
            // below its own content's width.
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: "auto",
          }}
        >
          <IconButton
            onClick={onToggleSidebar}
            aria-label="Toggle sidebar"
            sx={{
              width: {
                xs: 38,
                md: 48,
              },
              height: {
                xs: 38,
                md: 48,
              },
              borderRadius: "var(--radius-lg, 12px)",
              color: "var(--brand-maroon, #6d1530)",
              background: "var(--brand-maroon-soft, #fbeef1)",
              border: "1px solid rgba(109, 21, 48, 0.16)",
              flexShrink: 0,
              "&:hover": {
                background: "rgba(109, 21, 48, 0.16)",
              },
            }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            component="img"
            src="/assets/brand/jaipuria-it360-logo-trimmed.jpg"
            alt="Jaipuria IT360 Desk"
            sx={{
              height: { xs: 36, sm: 44, md: 54 },
              width: "auto",
              objectFit: "contain",
              flexShrink: 0,
              mixBlendMode: "multiply",
            }}
          />
        </Box>

        {/* Center Search */}
        <Box
          sx={{
            display: {
              xs: "none",
              lg: "flex",
            },
            alignItems: "center",
            width: "min(400px, 28vw)",
            height: 52,
            px: 2,
            borderRadius: "var(--radius-lg, 12px)",
            background: "#ffffff",
            border: "1px solid rgba(109, 21, 48, 0.14)",
            boxShadow: "0 2px 8px rgba(74, 14, 34, 0.04)",
          }}
        >
          <SearchIcon sx={{ color: "var(--text-muted, #8b877f)", mr: 1 }} />

          <InputBase
            placeholder="Search..."
            sx={{
              flex: 1,
              fontSize: 14,
              color: "var(--text-primary, #241f1a)",
              "& input::placeholder": {
                color: "var(--text-muted, #8b877f)",
                opacity: 1,
              },
            }}
          />
        </Box>

        {/* Right Side */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: {
              xs: 0.6,
              sm: 1,
              md: 1.2,
            },
            // flexGrow:1 to match the left-side Box above (see comment
            // there); flexShrink stays 0 and flexBasis "auto" exactly as
            // before, so this never shrinks below the profile cluster's own
            // width.
            flexGrow: 1,
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          <IconButton
            sx={{
              display: {
                xs: "none",
                sm: "inline-flex",
              },
              width: 50,
              height: 50,
              borderRadius: "var(--radius-lg, 12px)",
              color: "var(--text-primary, #241f1a)",
              background: "#ffffff",
              border: "1px solid rgba(109, 21, 48, 0.14)",
              "&:hover": {
                background: "var(--brand-maroon-soft, #fbeef1)",
                color: "var(--brand-maroon, #6d1530)",
              },
            }}
          >
            <Badge badgeContent={3} color="success">
              <NotificationsNoneOutlinedIcon />
            </Badge>
          </IconButton>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              gap: {
                xs: 0,
                md: 1.1,
              },
              pl: {
                xs: 0,
                sm: 0.5,
                md: 0.7,
              },
              pr: {
                xs: 0,
                sm: 0.7,
                md: 1.2,
              },
              py: {
                xs: 0,
                sm: 0.5,
                md: 0.6,
              },
              borderRadius: "var(--radius-lg, 12px)",
              background: {
                xs: "transparent",
                sm: "#ffffff",
              },
              border: {
                xs: "1px solid transparent",
                sm: "1px solid rgba(109, 21, 48, 0.14)",
              },
            }}
          >
            <Avatar
              sx={{
                width: {
                  xs: 34,
                  md: 42,
                },
                height: {
                  xs: 34,
                  md: 42,
                },
                bgcolor: "var(--brand-maroon, #6d1530)",
                color: "#fff",
                fontWeight: 700,
                boxShadow: "0 8px 18px rgba(109, 21, 48, 0.22)",
              }}
            >
              {user?.email?.charAt(0)?.toUpperCase() || "U"}
            </Avatar>

            <Box
              sx={{
                display: {
                  xs: "none",
                  md: "block",
                },
              }}
            >
              <Typography
                noWrap
                title={user?.email || "Admin User"}
                sx={{
                  fontSize: 13.5,
                  fontWeight: 800,
                  color: "#1f2f2d",
                  lineHeight: 1.2,
                  // A fixed px maxWidth still clipped routine college
                  // email addresses (e.g. "manish.singh@jaipuria.edu.in")
                  // at this bold 800-weight size. Sized in `ch` (tied to
                  // the font's own glyph width) instead, so it scales with
                  // the actual text rather than a guessed pixel budget.
                  // The native title attribute above still exposes the
                  // full address on hover/focus for the rarer address that
                  // still overflows.
                  maxWidth: "30ch",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user?.email || "Admin User"}
              </Typography>

              <Typography
                sx={{
                  fontSize: 11.5,
                  color: "var(--text-secondary, #475569)",
                  fontWeight: 500,
                  lineHeight: 1.2,
                }}
              >
                {user?.role || "Administrator"}
              </Typography>
            </Box>

            <KeyboardArrowDownOutlinedIcon
              sx={{
                display: {
                  xs: "none",
                  md: "block",
                },
                color: "#6c7b78",
                fontSize: 20,
              }}
            />
          </Box>

          {/* Root cause of "no way to log out on mobile": this button used
              to be display:{xs:"none", md:"inline-flex"} -- fully hidden
              below the md breakpoint with no alternative access point
              anywhere else in the header. It now stays visible at every
              width; only the text label collapses to an icon-only button
              on narrow screens so it doesn't compete for space with the
              title/search/notification/avatar cluster. */}
          <Button
            onClick={handleLogout}
            aria-label="Logout"
            variant="outlined"
            size="small"
            sx={{
              height: { xs: 42, md: 50 },
              minWidth: { xs: 42, md: "auto" },
              width: { xs: 42, md: "auto" },
              px: { xs: 0, md: 2.2 },
              borderRadius: "var(--radius-lg, 12px)",
              // Solid green fill made this the single most prominent
              // element in the header, outranking the page title and the
              // user's own profile. Kept as an outline (no box-shadow) so it
              // reads as an available action rather than the header's
              // primary call-to-action; white fill matches the other header
              // controls (search, bell, user card).
              borderColor: "rgba(109, 21, 48, 0.4)",
              color: "var(--brand-maroon, #6d1530)",
              bgcolor: "#ffffff",
              fontWeight: 700,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "var(--brand-maroon-soft, #fbeef1)",
                borderColor: "var(--brand-maroon, #6d1530)",
                boxShadow: "none",
              },
              "& .MuiButton-startIcon": {
                mx: { xs: 0, md: undefined },
              },
            }}
            startIcon={<LogoutOutlinedIcon sx={{ display: { xs: "none", md: "inline-flex" } }} />}
          >
            <LogoutOutlinedIcon sx={{ display: { xs: "inline-flex", md: "none" }, fontSize: 20 }} />
            <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
              Logout
            </Box>
          </Button>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;